import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useUsersAuthzAPIUserPermissionsReadCheckUserPermission } from '@mi-front/api-clients';
import { UserRole } from '@mi-front/core';
import React from 'react';
import { useAuth } from '../../authentication/hooks/useAuth';

interface Props {
  readonly permission: string;
  readonly cant?: React.ReactElement | (() => React.ReactElement);
  readonly children: React.ReactElement;
}

/**
 * IfPermission component that checks permissions against the UsersAuthz API.
 *
 * For users with CLIENT_USER role, it checks the permission against the UsersAuthz API.
 * For all other roles (CLIENT_ADMIN, SUPERADMIN), permission is automatically granted.
 *
 * @example
 * ```tsx
 * <IfPermission permission="content:create">
 *   <button>Create Content</button>
 * </IfPermission>
 *
 * <IfPermission
 *   permission="content:delete"
 *   cant={() => <span>You don't have permission to delete content</span>}
 * >
 *   <button>Delete Content</button>
 * </IfPermission>
 * ```
 */
export const IfPermission = (p: Props): React.ReactElement => {
  const auth = useAuth();

  if (!auth || !auth.user) {
    throw new Error('Auth context or user not found');
  }

  const permissionsEnabled = useFeatureIsOn('app__user_permissions_check');

  // Check if we need to verify permission (only for CLIENT_USER)
  const shouldCheckPermission = permissionsEnabled && auth.user.role === UserRole.CLIENT_USER;

  // For CLIENT_USER, check permission against UsersAuthz API
  const permissionCheck = useUsersAuthzAPIUserPermissionsReadCheckUserPermission(p.permission, {
    query: {
      enabled: shouldCheckPermission,
    },
  });

  // If user is not CLIENT_USER, grant permission automatically
  if (!shouldCheckPermission) {
    return p.children;
  }

  // Show loading state while checking permission
  if (permissionCheck.isLoading) {
    return <></>; // Return empty fragment for loading state
  }

  // Show error state if permission check failed
  if (permissionCheck.error) {
    // Log error but don't block UI - treat as no permission
    console.error('Permission check failed:', permissionCheck.error);
    return p.cant ? typeof p.cant === 'function' ? p.cant() : p.cant : <></>;
  }

  // Check if user has permission
  if (permissionCheck.data?.hasPermission) {
    return p.children;
  }

  // User doesn't have permission
  return p.cant ? typeof p.cant === 'function' ? p.cant() : p.cant : <></>;
};

IfPermission.defaultProps = {
  cant: () => <></>,
};
